import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} PurchaseOptionPickerRefs
 * @property {HTMLInputElement} sellingPlanField
 * @property {HTMLElement} [addToCartPrice]
 * @property {HTMLElement} [subscribePrice]
 * @property {HTMLElement} [subscribeCompareAtPrice]
 * @property {HTMLElement} [subscribePerServing]
 * @property {HTMLElement} [subscribeSaveBadge]
 * @property {HTMLElement} [oneTimePrice]
 * @property {HTMLElement} [oneTimePerServing]
 */

/**
 * @typedef {Object} Tier
 * @property {string|null} planId    Selling plan matching this pack, if any.
 * @property {number} oneTimePrice   The variant's own price, in cents.
 * @property {number|null} oneTimeCompareAtPrice
 * @property {number} planPrice      The subscription price for this pack, in cents.
 * @property {number|null} planCompareAtPrice
 * @property {number} savePercent
 * @property {number} servings       Servings in this pack (servings per unit x months).
 */

/**
 * Owns the supply-tier radios and the Subscribe & Save / One-time Purchase toggle for the
 * product-detail section.
 *
 * The tiers are VARIANTS, not selling plans. Picking one rewrites the form's hidden `id`
 * field as well as `selling_plan`, so the cart gets the pack the customer actually chose —
 * previously the tiers only set `selling_plan`, and every tier added the first variant.
 *
 * Everything is driven from a JSON blob rendered alongside the radios (one entry per
 * variant), so no fetch or event-morph plumbing is needed.
 *
 * @extends {Component<PurchaseOptionPickerRefs>}
 */
class PurchaseOptionPicker extends Component {
  requiredRefs = ['sellingPlanField'];

  connectedCallback() {
    super.connectedCallback();
    /** @type {Record<string, Tier>} */
    this.tiers = {};
    const tiersScript = this.querySelector('script[data-plans-json]');
    if (tiersScript instanceof HTMLScriptElement) {
      try {
        this.tiers = JSON.parse(tiersScript.textContent ?? '{}');
      } catch {
        this.tiers = {};
      }
    }
    this.update();
  }

  /** @param {number} cents */
  formatPrice(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * @param {number} cents
   * @param {number} [servings] Servings in the pack; falls back to one unit.
   */
  perServing(cents, servings) {
    const count = servings || Number(this.dataset.servingsPerUnit || 0);
    if (count <= 0) return null;
    return `${this.formatPrice(Math.round(cents / count))} per serving`;
  }

  /**
   * Every tier card shows what its pack costs under the selected purchase type:
   * the subscription price (one-time price struck through) or the one-time price.
   * @param {boolean} isSubscribing
   */
  updateTierCards(isSubscribing) {
    for (const input of this.querySelectorAll('input[name="purchase-plan-tier"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const tier = this.tiers[input.value];
      const card = input.closest('.product-detail-tier');
      if (!tier || !card) continue;

      const subscribed = isSubscribing && tier.planId != null;
      const cents = subscribed ? tier.planPrice : tier.oneTimePrice;
      const compare = subscribed ? tier.planCompareAtPrice : tier.oneTimeCompareAtPrice;

      const price = card.querySelector('[data-tier-price]');
      if (price) price.textContent = this.formatPrice(cents);

      const compareEl = card.querySelector('[data-tier-compare-price]');
      if (compareEl instanceof HTMLElement) {
        const showCompare = compare != null && compare > cents;
        compareEl.hidden = !showCompare;
        if (showCompare) compareEl.textContent = this.formatPrice(/** @type {number} */ (compare));
      }

      const perServing = card.querySelector('[data-tier-per-serving]');
      const label = this.perServing(cents, tier.servings);
      if (perServing && label) perServing.textContent = label;
    }
  }

  update() {
    const tierInput = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="purchase-plan-tier"]:checked')
    );
    const typeInput = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="purchase-type"]:checked')
    );

    const variantId = tierInput?.value ?? null;
    const isSubscribing = typeInput?.value === 'subscribe';
    const tier = variantId ? this.tiers[variantId] : null;

    // The variant field is on the <form>, outside this component — refs do not cross
    // component roots, so it is queried rather than declared as a ref.
    const variantField = this.closest('form')?.querySelector('input[name="id"]');
    if (variantField instanceof HTMLInputElement && variantId) {
      variantField.value = variantId;
    }

    const planId = tier?.planId ?? null;
    this.refs.sellingPlanField.value = isSubscribing && planId ? planId : '';

    const oneTimeCents = tier ? tier.oneTimePrice : Number(this.dataset.basePrice || 0);
    const planCents = tier && tier.planPrice != null ? tier.planPrice : oneTimeCents;
    const activeCents = isSubscribing && planId ? planCents : oneTimeCents;

    // Not a `ref`: the add-to-cart button lives inside the nested `<add-to-cart-component>`,
    // which is itself a Component and would claim this element as its own ref instead.
    this.updateTierCards(isSubscribing);

    const addToCartPrice = this.querySelector('[data-add-to-cart-price]');
    if (addToCartPrice) {
      addToCartPrice.textContent = this.formatPrice(activeCents);
    }

    // One-time row — tracks the selected pack, not the page's default variant.
    if (this.refs.oneTimePrice) {
      this.refs.oneTimePrice.textContent = this.formatPrice(oneTimeCents);
    }
    if (this.refs.oneTimePerServing) {
      const label = this.perServing(oneTimeCents, tier?.servings);
      if (label) this.refs.oneTimePerServing.textContent = label;
    }

    if (!tier) return;

    // Subscribe row.
    if (this.refs.subscribePrice) {
      this.refs.subscribePrice.textContent = this.formatPrice(planCents);
    }
    if (this.refs.subscribeCompareAtPrice) {
      const compare = tier.planCompareAtPrice;
      const showCompare = compare != null && compare > planCents;
      this.refs.subscribeCompareAtPrice.hidden = !showCompare;
      if (showCompare) {
        this.refs.subscribeCompareAtPrice.textContent = this.formatPrice(/** @type {number} */ (compare));
      }
    }
    if (this.refs.subscribePerServing) {
      const label = this.perServing(planCents, tier.servings);
      if (label) this.refs.subscribePerServing.textContent = label;
    }
    if (this.refs.subscribeSaveBadge) {
      const savePercent = tier.savePercent || 0;
      this.refs.subscribeSaveBadge.hidden = savePercent <= 0;
      if (savePercent > 0) {
        this.refs.subscribeSaveBadge.textContent = `Save ${savePercent}%`;
      }
    }
  }
}

if (!customElements.get('purchase-option-picker')) {
  customElements.define('purchase-option-picker', PurchaseOptionPicker);
}
